import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import {
  Bell,
  Boxes,
  Bot,
  CircleDollarSign,
  FileText,
  Headphones,
  LayoutDashboard,
  ListOrdered,
  Mail,
  Menu,
  MessageSquare,
  Moon,
  Music,
  Package,
  PencilRuler,
  Plug2,
  Radio,
  Salad,
  Share2,
  ShoppingCart,
  Gift,
  Activity,
  Sparkles,
  Shield,
  Siren,
  Trophy,
  Users,
  Wind,
  CreditCard,
  CalendarDays,
  Heart,
  Tag,
} from "lucide-react";

type TabValue = "dashboard" | "requests" | "templates" | "settings";
type SettingsSub = "mail" | "payment" | "membership" | "security";

type CommonProps = {
  isDark: boolean;
  onToggleTheme: () => void;
  /** Desktop: md+ göster. Mobile: Sheet içine konur. */
  variant?: "desktop" | "mobile";
  /** Mobile sheet kullanırken seçim sonrası menüyü kapatmak için. */
  onNavigate?: () => void;
};

type TabsModeProps = CommonProps & {
  mode: "tabs";
  tab: TabValue;
  setTab: (tab: TabValue) => void;
  settingsSub: SettingsSub;
  setSettingsSub: (sub: SettingsSub) => void;
};

type LinksModeProps = CommonProps & {
  mode: "links";
};

export type NumerolojiAdminSidebarProps = TabsModeProps | LinksModeProps;

export function NumerolojiAdminSidebar(props: NumerolojiAdminSidebarProps) {
  const { pathname } = useLocation();

  const baseItem =
    "flex h-auto w-full items-center justify-start gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-200 hover:bg-white/5 hover:text-slate-50 focus-visible:ring-0 focus-visible:ring-offset-0";

  const activeItem = "bg-blue-600/20 text-white ring-inset ring-1 ring-blue-500/30 shadow-none";

  const activeLinkKey:
    | "users"
    | "manual"
    | "detail"
    | "products"
    | "packages"
    | "meditations"
    | "breaths"
    | "diets"
    | "activities"
    | "rituals"
    | "reviews"
    | "orders"
    | "support"
    | "reports"
    | "badges"
    | "frekanslar"
    | "onboarding"
    | "abonelik"
    | "kombinasyonlar"
    | "iliskianalizi"
    | "muzik"
    | "social"
    | "socialgen"
    | "lunasettings"
    | "socialconn"
    | "kategoriler"
    | "bildirimler"
    | "contentpurchases"
    | "icerikSatinAlmalari"
    | "other" = pathname.startsWith("/admin/users")
    ? "users"
    : pathname.startsWith("/admin/numeroloji/yeni")
    ? "manual"
    : pathname.startsWith("/admin/numeroloji/yorumlar")
      ? "reviews"
      : pathname.startsWith("/admin/numeroloji/siparisler")
        ? "orders"
        : pathname.startsWith("/admin/numeroloji/destek")
          ? "support"
          : pathname.startsWith("/admin/numeroloji/sikayetler")
            ? "reports"
          : pathname.startsWith("/admin/numeroloji/rozetler")
            ? "badges"
          : pathname.startsWith("/admin/numeroloji/frekans")
            ? "frekanslar"
          : pathname.startsWith("/admin/numeroloji/onboarding-yorumlar")
            ? "onboarding"
          : pathname.startsWith("/admin/numeroloji/abonelik-planlari")
            ? "abonelik"
          : pathname.startsWith("/admin/numeroloji/kombinasyonlar")
            ? "kombinasyonlar"
          : pathname.startsWith("/admin/social/content-generator")
            ? "socialgen"
          : pathname.startsWith("/admin/social/luna-settings")
            ? "lunasettings"
          : pathname.startsWith("/admin/social/connections")
            ? "socialconn"
          : pathname.startsWith("/admin/social")
            ? "social"
          : pathname.startsWith("/admin/numeroloji/meditasyon-muzik")
            ? "muzik"
          : pathname.startsWith("/admin/numeroloji/iliski-analizi")
            ? "iliskianalizi"
          : pathname.startsWith("/admin/kategoriler")
            ? "kategoriler"
          : pathname.startsWith("/admin/numeroloji/aktivite")
          ? "activities"
          : pathname.startsWith("/admin/numeroloji/rituel")
            ? "rituals"
          : pathname.startsWith("/admin/numeroloji/diyet")
            ? "diets"
            : pathname.startsWith("/admin/numeroloji/meditasyon")
              ? "meditations"
            : pathname.startsWith("/admin/numeroloji/nefes")
              ? "breaths"
            : pathname.startsWith("/admin/numeroloji/urun")
              ? "products"
              : pathname.startsWith("/admin/numeroloji/paket")
                ? "packages"
                : pathname.startsWith("/admin/bildirimler")
                  ? "bildirimler"
                  : pathname.startsWith("/admin/content-purchases")
                  ? "contentpurchases"
                  : pathname.startsWith("/admin/icerik-satin-almalari")
                  ? "icerikSatinAlmalari"
                  : pathname.startsWith("/admin/numeroloji/") && pathname !== "/admin/numeroloji"
                    ? "detail"
                    : "other";

  const displayClass = props.variant === "mobile" ? "flex" : "hidden md:flex";
  const handleNavigate = () => props.onNavigate?.();

  return (
    <aside
      className={cn(
        displayClass,
        "w-[260px] shrink-0 flex-col border-r border-slate-800/60 bg-gradient-to-b from-slate-900 to-slate-950 text-slate-200",
      )}
    >
      <div className="flex items-center gap-2 px-4 py-5">
        <div
          className={cn(
            "grid h-9 w-9 place-items-center rounded-xl ring-1",
            "bg-blue-600/20 text-blue-200 ring-blue-500/30",
          )}
        >
          <FileText className="h-4 w-4" />
        </div>
        <div className={cn("text-base font-semibold tracking-tight text-slate-100")}>Numeroloji</div>
      </div>

      <div className="px-3">
        {props.mode === "tabs" ? (
          <div className="grid gap-1">
            <TabsList className="grid h-auto w-full gap-1 bg-transparent p-0 text-slate-200 justify-start items-stretch">
              <TabsTrigger
                value="dashboard"
                onClick={handleNavigate}
                className={cn(
                  "w-full justify-start gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-200 hover:bg-white/5 hover:text-slate-50 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:bg-blue-600/20 data-[state=active]:text-white data-[state=active]:ring-1 data-[state=active]:ring-inset data-[state=active]:ring-blue-500/30 data-[state=active]:shadow-none",
                )}
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </TabsTrigger>

              <TabsTrigger
                value="requests"
                onClick={handleNavigate}
                className={cn(
                  "w-full justify-start gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-200 hover:bg-white/5 hover:text-slate-50 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:bg-blue-600/20 data-[state=active]:text-white data-[state=active]:ring-1 data-[state=active]:ring-inset data-[state=active]:ring-blue-500/30 data-[state=active]:shadow-none",
                )}
              >
                <ListOrdered className="h-4 w-4" />
                İstek Listesi
              </TabsTrigger>
            </TabsList>

            <Button asChild variant="ghost" className={baseItem}>
              <Link onClick={handleNavigate} to="/admin/numeroloji/yeni">
                <PencilRuler className="h-4 w-4" />
                Manuel İstek
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "reviews" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/yorumlar">
                <MessageSquare className="h-4 w-4" />
                Yorum Onayı
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "orders" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/siparisler">
                <ShoppingCart className="h-4 w-4" />
                Siparişler
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "support" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/destek">
                <MessageSquare className="h-4 w-4" />
                Destek Ticketları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "reports" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/sikayetler">
                <Siren className="h-4 w-4" />
                Şikayetler
              </Link>
            </Button>

            <TabsList className="grid h-auto w-full gap-1 bg-transparent p-0 text-slate-200 justify-start items-stretch">
              <TabsTrigger
                value="templates"
                onClick={handleNavigate}
                className={cn(
                  "w-full justify-start gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-200 hover:bg-white/5 hover:text-slate-50 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:bg-blue-600/20 data-[state=active]:text-white data-[state=active]:ring-1 data-[state=active]:ring-inset data-[state=active]:ring-blue-500/30 data-[state=active]:shadow-none",
                )}
              >
                <FileText className="h-4 w-4" />
                Şablon Listesi
              </TabsTrigger>
            </TabsList>

            <div className={cn("pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80")}>Ayarlar</div>

            <button
              type="button"
              onClick={() => {
                props.setSettingsSub("mail");
                props.setTab("settings");
                handleNavigate();
              }}
              className={cn(
                baseItem,
                "text-left",
                props.tab === "settings" && props.settingsSub === "mail" && activeItem,
              )}
            >
              <Mail className="h-4 w-4" />
              Mail Ayarları
            </button>

            <button
              type="button"
              onClick={() => {
                props.setSettingsSub("payment");
                props.setTab("settings");
                handleNavigate();
              }}
              className={cn(
                baseItem,
                "text-left",
                props.tab === "settings" && props.settingsSub === "payment" && activeItem,
              )}
            >
              <CircleDollarSign className="h-4 w-4" />
              Ödeme Ayarları
            </button>

            <button
              type="button"
              onClick={() => {
                props.setSettingsSub("membership");
                props.setTab("settings");
                handleNavigate();
              }}
              className={cn(
                baseItem,
                "text-left",
                props.tab === "settings" && props.settingsSub === "membership" && activeItem,
              )}
            >
              <Sparkles className="h-4 w-4" />
              Üyelik Planları
            </button>

            <button
              type="button"
              onClick={() => {
                props.setSettingsSub("security");
                props.setTab("settings");
                handleNavigate();
              }}
              className={cn(
                baseItem,
                "text-left",
                props.tab === "settings" && props.settingsSub === "security" && activeItem,
              )}
            >
              <Shield className="h-4 w-4" />
              Güvenlik
            </button>

            <div className={cn("pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80")}>Katalog</div>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "products" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/urun">
                <Package className="h-4 w-4" />
                Ürün Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "packages" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/paket">
                <Boxes className="h-4 w-4" />
                Paket Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "meditations" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/meditasyon">
                <Headphones className="h-4 w-4" />
                Meditasyon Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "breaths" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/nefes">
                <Wind className="h-4 w-4" />
                Nefes Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "diets" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/diyet">
                <Salad className="h-4 w-4" />
                Diyet Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "activities" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/aktivite">
                <Activity className="h-4 w-4" />
                Aktivite Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "rituals" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/rituel">
                <Sparkles className="h-4 w-4" />
                Ritüel Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "frekanslar" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/frekans">
                <Radio className="h-4 w-4" />
                Frekans Setleri
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "muzik" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/meditasyon-muzik">
                <Music className="h-4 w-4" />
                Meditasyon Müziği
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "onboarding" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/onboarding-yorumlar">
                <Moon className="h-4 w-4" />
                Onboarding Yorumları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "abonelik" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/abonelik-planlari">
                <CreditCard className="h-4 w-4" />
                Abonelik Planları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "kombinasyonlar" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/kombinasyonlar">
                <CalendarDays className="h-4 w-4" />
                Günlük Kombinasyonlar
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "iliskianalizi" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/iliski-analizi">
                <Heart className="h-4 w-4" />
                İlişki Analizi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "contentpurchases" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/content-purchases">
                <ShoppingCart className="h-4 w-4" />
                İçerik Satışları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "icerikSatinAlmalari" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/icerik-satin-almalari">
                <Gift className="h-4 w-4" />
                İçerik Satın Almaları
              </Link>
            </Button>

            <div className={cn("pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80")}>Sosyal Medya</div>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "social" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social">
                <Share2 className="h-4 w-4" />
                SM Dashboard
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "socialgen" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social/content-generator">
                <Sparkles className="h-4 w-4" />
                İçerik Üretici
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "lunasettings" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social/luna-settings">
                <Bot className="h-4 w-4" />
                Luna Ayarları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "socialconn" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social/connections">
                <Plug2 className="h-4 w-4" />
                Bağlantılar
              </Link>
            </Button>

            <div className={cn("pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80")}>Sistem</div>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "badges" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/rozetler">
                <Trophy className="h-4 w-4" />
                Rozet Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "kategoriler" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/kategoriler">
                <Tag className="h-4 w-4" />
                Kategoriler
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "users" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/users">
                <Users className="h-4 w-4" />
                Kullanıcı Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "bildirimler" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/bildirimler">
                <Bell className="h-4 w-4" />
                Toplu Bildirim
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-1">
            <Button asChild variant="ghost" className={baseItem}>
              <Link onClick={handleNavigate} to="/admin/numeroloji?tab=dashboard">
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "detail" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji?tab=requests">
                <ListOrdered className="h-4 w-4" />
                İstek Listesi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "manual" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/yeni">
                <PencilRuler className="h-4 w-4" />
                Manuel İstek
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "reviews" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/yorumlar">
                <MessageSquare className="h-4 w-4" />
                Yorum Onayı
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "orders" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/siparisler">
                <ShoppingCart className="h-4 w-4" />
                Siparişler
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "support" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/destek">
                <MessageSquare className="h-4 w-4" />
                Destek Ticketları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "reports" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/sikayetler">
                <Siren className="h-4 w-4" />
                Şikayetler
              </Link>
            </Button>

            <Button asChild variant="ghost" className={baseItem}>
              <Link onClick={handleNavigate} to="/admin/numeroloji?tab=templates">
                <FileText className="h-4 w-4" />
                Şablon Listesi
              </Link>
            </Button>

            <div className={cn("pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80")}>Ayarlar</div>

            <Button asChild variant="ghost" className={baseItem}>
              <Link onClick={handleNavigate} to="/admin/numeroloji?tab=settings&sub=mail">
                <Mail className="h-4 w-4" />
                Mail Ayarları
              </Link>
            </Button>

            <Button asChild variant="ghost" className={baseItem}>
              <Link onClick={handleNavigate} to="/admin/numeroloji?tab=settings&sub=payment">
                <CircleDollarSign className="h-4 w-4" />
                Ödeme Ayarları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "products" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/urun">
                <Package className="h-4 w-4" />
                Ürün Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "packages" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/paket">
                <Boxes className="h-4 w-4" />
                Paket Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "meditations" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/meditasyon">
                <Headphones className="h-4 w-4" />
                Meditasyon Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "breaths" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/nefes">
                <Wind className="h-4 w-4" />
                Nefes Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "diets" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/diyet">
                <Salad className="h-4 w-4" />
                Diyet Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "activities" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/aktivite">
                <Activity className="h-4 w-4" />
                Aktivite Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "rituals" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/rituel">
                <Sparkles className="h-4 w-4" />
                Ritüel Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "frekanslar" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/frekans">
                <Radio className="h-4 w-4" />
                Frekans Setleri
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "muzik" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/meditasyon-muzik">
                <Music className="h-4 w-4" />
                Meditasyon Müziği
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "onboarding" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/onboarding-yorumlar">
                <Moon className="h-4 w-4" />
                Onboarding Yorumları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "abonelik" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/abonelik-planlari">
                <CreditCard className="h-4 w-4" />
                Abonelik Planları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "kombinasyonlar" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/kombinasyonlar">
                <CalendarDays className="h-4 w-4" />
                Günlük Kombinasyonlar
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "iliskianalizi" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/iliski-analizi">
                <Heart className="h-4 w-4" />
                İlişki Analizi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "contentpurchases" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/content-purchases">
                <ShoppingCart className="h-4 w-4" />
                İçerik Satışları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "icerikSatinAlmalari" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/icerik-satin-almalari">
                <Gift className="h-4 w-4" />
                İçerik Satın Almaları
              </Link>
            </Button>

            <div className={cn("pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80")}>Sosyal Medya</div>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "social" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social">
                <Share2 className="h-4 w-4" />
                SM Dashboard
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "socialgen" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social/content-generator">
                <Sparkles className="h-4 w-4" />
                İçerik Üretici
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "lunasettings" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social/luna-settings">
                <Bot className="h-4 w-4" />
                Luna Ayarları
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "socialconn" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/social/connections">
                <Plug2 className="h-4 w-4" />
                Bağlantılar
              </Link>
            </Button>

            <div className={cn("pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400/80")}>Sistem</div>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "badges" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/numeroloji/rozetler">
                <Trophy className="h-4 w-4" />
                Rozet Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "kategoriler" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/kategoriler">
                <Tag className="h-4 w-4" />
                Kategoriler
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "users" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/users">
                <Users className="h-4 w-4" />
                Kullanıcı Yönetimi
              </Link>
            </Button>

            <Button
              asChild
              variant="ghost"
              className={cn(baseItem, activeLinkKey === "bildirimler" && activeItem)}
            >
              <Link onClick={handleNavigate} to="/admin/bildirimler">
                <Bell className="h-4 w-4" />
                Toplu Bildirim
              </Link>
            </Button>
          </div>
        )}
      </div>

      <div className={cn("mt-auto border-t border-white/10 p-3") }>
        <button
          type="button"
          onClick={() => {
            props.onToggleTheme();
            handleNavigate();
          }}
          className={cn(
            "flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-200 hover:bg-white/5 hover:text-slate-50 focus-visible:ring-0 focus-visible:ring-offset-0",
          )}
        >
          <Moon className="h-4 w-4" />
          Mod Değiştir
        </button>
      </div>
    </aside>
  );
}

export function NumerolojiAdminMobileSidebar(props: NumerolojiAdminSidebarProps) {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" className="text-slate-500 lg:hidden" aria-label="Menü">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>

      <SheetContent side="left" className="w-[280px] border-0 bg-transparent p-0">
        <SheetHeader className="sr-only">
          <SheetTitle>Admin menüsü</SheetTitle>
        </SheetHeader>
        <NumerolojiAdminSidebar {...(props as any)} variant="mobile" onNavigate={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}
